import { useEffect, useMemo, useState } from 'react';
import { App } from 'antd';
import type { TableProps } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileAddOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  VerticalAlignBottomOutlined,
} from '@ant-design/icons';
import { Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { DataTable, MenuThaoTac, PageHeader, StatTile } from '../../../components/shared';
import type { MucThaoTac } from '../../../components/shared/MenuThaoTac';
import { Badge, Button, Empty, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { luuTep } from '../../../lib/luu-tep';
import { useToast } from '../../../lib/toast';
import { SoSanhTomTatDialog } from '../components/tom-tat/SoSanhTomTatDialog';
import { ThemVanBanDialog } from '../components/tom-tat/ThemVanBanDialog';
import {
  NHAN_NGUON_VAN_BAN,
  TONG_CHUA_TOM_TAT,
  TONG_DA_TOM_TAT,
  TONG_NGUON_VAN_BAN,
} from '../constants';
import { useQuyenVanBan } from '../hooks/useQuyenVanBan';
import {
  useChayTomTat,
  useDanhSachTomTat,
  useDocTomTat,
  useThemTomTat,
  useXoaTomTat,
} from '../hooks/useTomTat';
import { LOAI_VAN_BAN } from '../nghiep-vu/loai-van-ban';
import { daTomTat, dangXuLy, docXuatTomTat, tenTepXuatTomTat, tyLeRutGon } from '../nghiep-vu/tom-tat';
import type { TomTat } from '../types-tom-tat';

type LocTrangThai = 'done' | 'pending';

/** Tóm tắt văn bản (`/xu-ly-van-ban/tom-tat`), chuyển từ foxainative. */
export function TomTatView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const navigate = useNavigate();
  const { modal } = App.useApp();
  const { data, isLoading, error, refetch } = useDanhSachTomTat();
  const docTomTat = useDocTomTat();
  const them = useThemTomTat();
  const chay = useChayTomTat();
  const xoa = useXoaTomTat();

  const { canCreate, canUpdate, canDelete } = useQuyenVanBan('summary');
  const coTheSoan = useQuyenVanBan('compose').canCreate;
  const coThePhanCong = useQuyenVanBan('task-assignment').canCreate;

  const [tuKhoa, setTuKhoa] = useState('');
  const [loai, setLoai] = useState<string | undefined>();
  const [trangThai, setTrangThai] = useState<LocTrangThai | undefined>();
  const [moThem, setMoThem] = useState(false);
  const [xem, setXem] = useState<{ id: string; sua: boolean } | null>(null);
  /** Văn bản vừa gửi tóm tắt, theo dõi tới khi xong: `true` = mở hộp so sánh khi xong (như bản cũ). */
  const [theoDoi, setTheoDoi] = useState<Record<string, boolean>>({});

  const tatCa = data?.data;
  // Tác vụ nền xong hay lỗi: mở hộp so sánh hoặc báo câu lỗi của backend
  useEffect(() => {
    if (!tatCa) return;
    const xong: string[] = [];
    for (const [id, moKhiXong] of Object.entries(theoDoi)) {
      const r = tatCa.find((x) => x.id === id);
      if (!r) {
        xong.push(id);
      } else if (r.status === 'COMPLETED') {
        xong.push(id);
        if (moKhiXong) setXem({ id, sua: false });
      } else if (r.status === 'FAILED') {
        xong.push(id);
        void docTomTat(id).then(
          (d) => toast.error(d.error_message || t('vanBanTomTat.thatBai')),
          (e: unknown) => toast.error(dichLoi(e)),
        );
      }
    }
    if (xong.length) {
      setTheoDoi((cu) => {
        const moi = { ...cu };
        xong.forEach((id) => delete moi[id]);
        return moi;
      });
    }
  }, [tatCa, theoDoi, docTomTat, toast, t, dichLoi]);

  const rows = useMemo(() => {
    const q = tuKhoa.trim().toLowerCase();
    return (tatCa ?? []).filter(
      (d) =>
        (!q || d.document_name.toLowerCase().includes(q)) &&
        (!loai || d.document_type === loai) &&
        (!trangThai || (trangThai === 'done') === daTomTat(d.status)),
    );
  }, [tatCa, tuKhoa, loai, trangThai]);
  const coLoc = Boolean(tuKhoa.trim() || loai || trangThai);
  const thongKe = data?.stats;
  const so = (n: number | undefined) => soVN(n ?? 0, locale);
  const tyLeTb = thongKe?.avg_reduction_ratio;

  const chayAi = (id: string) => {
    chay.mutate(id, { onSuccess: () => setTheoDoi((cu) => ({ ...cu, [id]: true })) });
  };

  const xuat = (id: string) => {
    void docTomTat(id).then(
      (d) => {
        if (!d.summary_content) return;
        luuTep(new Blob(['﻿' + docXuatTomTat(d)], { type: 'application/msword' }), tenTepXuatTomTat(d.document_name));
      },
      (e: unknown) => toast.error(dichLoi(e)),
    );
  };

  const soanTu = (id: string) => navigate(`/xu-ly-van-ban/soan-thao?src=${encodeURIComponent(id)}`);

  const hoiXoa = (d: TomTat) => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: t('vanBanTomTat.xacNhanXoa', { ten: d.document_name }),
      content: t('vanBanTomTat.xacNhanXoaMo'),
      okText: t('vanBanTomTat.xoa'),
      okButtonProps: { danger: true },
      cancelText: t('chung.huy'),
      onOk: () =>
        xoa.mutateAsync(d.id).then(() => {
          if (xem?.id === d.id) setXem(null);
        }),
    });
  };

  const columns: TableProps<TomTat>['columns'] = [
    {
      title: t('chung.stt'),
      width: 64,
      render: (_v, _r, i) => <span className="text-xs tabular-nums text-content-muted">{i + 1}</span>,
    },
    {
      title: t('vanBanTomTat.cot.ten'),
      width: 420,
      render: (_v, d) => {
        const tyLe = tyLeRutGon(d.reduction_ratio);
        return (
          <div className="min-w-0 max-w-sm">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="truncate font-medium text-content-primary" title={d.document_name}>
                {d.document_name}
              </span>
              {d.source && (
                <span
                  className="shrink-0"
                  title={d.external_doc_id ? t('vanBanTomTat.maDoiTac', { ma: d.external_doc_id }) : undefined}
                >
                  <Badge variant={TONG_NGUON_VAN_BAN} className="whitespace-nowrap">
                    {t(NHAN_NGUON_VAN_BAN[d.source])}
                  </Badge>
                </span>
              )}
            </div>
            <div className="mt-0.5 truncate text-xs text-content-muted">
              {t('vanBanTomTat.kyTuGoc', { so: soVN(d.original_char_count ?? 0, locale) })}
              {daTomTat(d.status) && tyLe !== null ? ` • ${t('vanBanTomTat.daRutGon', { so: tyLe })}` : ''}
            </div>
          </div>
        );
      },
    },
    {
      title: t('vanBanTomTat.cot.loai'),
      width: 112,
      render: (_v, d) => (
        <Badge variant="neutral" className="whitespace-nowrap">
          {d.document_type}
        </Badge>
      ),
    },
    {
      title: t('vanBanTomTat.cot.ngayTao'),
      width: 104,
      render: (_v, d) => <span className="whitespace-nowrap text-xs text-content-muted">{ngayVN(d.created_at)}</span>,
    },
    {
      title: t('vanBanTomTat.cot.nguoiTao'),
      width: 140,
      render: (_v, d) => <span className="text-xs text-content-secondary">{d.creator_name || t('chung.khongCo')}</span>,
    },
    {
      title: t('chung.trangThai'),
      width: 128,
      render: (_v, d) =>
        daTomTat(d.status) ? (
          <Badge variant={TONG_DA_TOM_TAT} className="whitespace-nowrap">
            {t('vanBanTomTat.daTomTat')}
          </Badge>
        ) : (
          <Badge variant={TONG_CHUA_TOM_TAT} className="whitespace-nowrap">
            {t('vanBanTomTat.chuaTomTat')}
          </Badge>
        ),
    },
    {
      title: t('chung.thaoTac'),
      width: 180,
      align: 'center',
      render: (_v, d) => {
        const xong = daTomTat(d.status);
        const dangChay = dangXuLy(d.status) || (chay.isPending && chay.variables === d.id);
        const muc: MucThaoTac[] = xong
          ? [
              { khoa: 'xem', nhan: t('vanBanTomTat.xemSoSanh'), icon: <EyeOutlined className="text-violet-500" />, nhom: 'xem', onChon: () => setXem({ id: d.id, sua: false }) },
              { khoa: 'sua', nhan: t('vanBanTomTat.suaBanTomTat'), icon: <EditOutlined className="text-primary-600" />, hien: canUpdate, nhom: 'sua', onChon: () => setXem({ id: d.id, sua: true }) },
              { khoa: 'xuat', nhan: t('vanBanTomTat.xuatDoc'), icon: <DownloadOutlined className="text-teal-500" />, nhom: 'xem', onChon: () => xuat(d.id) },
              { khoa: 'soan', nhan: t('vanBanTomTat.soanTuBanNay'), icon: <FileAddOutlined className="text-violet-600" />, hien: coTheSoan, nhom: 'sua', onChon: () => soanTu(d.id) },
              { khoa: 'phan-cong', nhan: t('vanBanTomTat.aiPhanCong'), icon: <TeamOutlined className="text-success-600" />, hien: coThePhanCong, nhom: 'sua', onChon: () => setXem({ id: d.id, sua: false }) },
              { khoa: 'xoa', nhan: t('vanBanTomTat.xoa'), icon: <DeleteOutlined />, hien: canDelete, nhom: 'pha-huy', onChon: () => hoiXoa(d) },
            ]
          : [
              { khoa: 'soan', nhan: t('vanBanTomTat.soanTuBanNay'), icon: <FileAddOutlined className="text-violet-600" />, hien: coTheSoan, nhom: 'sua', onChon: () => soanTu(d.id) },
              { khoa: 'xoa', nhan: t('vanBanTomTat.xoa'), icon: <DeleteOutlined />, hien: canDelete, nhom: 'pha-huy', onChon: () => hoiXoa(d) },
            ];
        return (
          <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
            {!xong && canCreate && (
              <Button
                variant="primary"
                size="sm"
                className="ai-glow"
                loading={dangChay}
                icon={<Sparkles size={14} className="ai-sparkle" />}
                onClick={() => chayAi(d.id)}
              >
                {dangChay ? t('vanBanTomTat.dangTomTatNgan') : t('vanBanTomTat.aiTomTat')}
              </Button>
            )}
            <MenuThaoTac tenBanGhi={d.document_name} muc={muc} />
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.muc.xuLyVanBan') }, { label: t('vanBanTomTat.tieuDe') }]}
          titleIcon={<FileTextOutlined className="text-primary-600" />}
          title={t('vanBanTomTat.tieuDe')}
          subtitle={t('vanBanTomTat.moTa')}
          actions={
            <>
              <Button variant="ghost" title={t('chung.lamMoi')} aria-label={t('chung.lamMoi')} onClick={() => void refetch()}>
                <ReloadOutlined className={isLoading ? 'animate-spin' : ''} />
              </Button>
              {canCreate && (
                <Button
                  variant="primary"
                  icon={<FileAddOutlined />}
                  onClick={() => {
                    them.reset();
                    setMoThem(true);
                  }}
                >
                  {t('vanBanTomTat.them')}
                </Button>
              )}
            </>
          }
        />

        <div className="grid grid-cols-12 gap-4">
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-3" nhan={t('vanBanTomTat.thongKe.tong')} so={so(thongKe?.total)} icon={<FileTextOutlined />} tone="primary" />
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-3" nhan={t('vanBanTomTat.thongKe.daTomTat')} so={so(thongKe?.summarized)} icon={<CheckCircleOutlined />} tone="success" />
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-3" nhan={t('vanBanTomTat.thongKe.chuaTomTat')} so={so(thongKe?.pending)} icon={<ClockCircleOutlined />} tone="warning" />
          <StatTile
            span="col-span-12 sm:col-span-6 lg:col-span-3"
            nhan={t('vanBanTomTat.thongKe.tyLe')}
            so={tyLeTb != null ? `${Math.round(tyLeTb)}%` : t('chung.khongCo')}
            icon={<VerticalAlignBottomOutlined />}
            tone="violet"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Input
            className="min-w-[200px] max-w-sm flex-1"
            allowClear
            prefix={<SearchOutlined className="text-primary-500" />}
            placeholder={t('vanBanTomTat.timKiem')}
            aria-label={t('vanBanTomTat.timKiem')}
            value={tuKhoa}
            onChange={(e) => setTuKhoa(e.target.value)}
          />
          <Select
            allowClear
            className="min-w-[12rem]"
            placeholder={t('vanBanTomTat.tatCaLoai')}
            aria-label={t('vanBanTomTat.locLoai')}
            options={LOAI_VAN_BAN.map((x) => ({ value: x, label: x }))}
            value={loai}
            onChange={(v) => setLoai(v as string | undefined)}
          />
          <Select
            allowClear
            className="min-w-[12rem]"
            placeholder={t('vanBanTomTat.tatCaTrangThai')}
            aria-label={t('vanBanTomTat.locTrangThai')}
            options={[
              { value: 'done', label: t('vanBanTomTat.daTomTat') },
              { value: 'pending', label: t('vanBanTomTat.chuaTomTat') },
            ]}
            value={trangThai}
            onChange={(v) => setTrangThai(v as LocTrangThai | undefined)}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {error ? (
            <div role="alert" className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="text-sm text-danger-700">{t('vanBanTomTat.loiDanhSach', { loi: dichLoi(error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <DataTable<TomTat>
              caption={t('vanBanTomTat.bangCaption')}
              rowKey="id"
              dataSource={rows}
              columns={columns}
              loading={isLoading}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              onRow={(d) => ({
                onClick: () => daTomTat(d.status) && setXem({ id: d.id, sua: false }),
                className: daTomTat(d.status) ? 'cursor-pointer' : '',
              })}
              locale={{
                emptyText: isLoading ? (
                  ' '
                ) : (
                  <Empty
                    icon={FileTextOutlined}
                    title={t(coLoc ? 'vanBanTomTat.rongLoc' : 'vanBanTomTat.rongThat')}
                    description={coLoc ? undefined : t('vanBanTomTat.rongThatMo')}
                    action={
                      coLoc || !canCreate ? undefined : (
                        <Button variant="primary" icon={<FileAddOutlined />} onClick={() => setMoThem(true)}>
                          {t('vanBanTomTat.them')}
                        </Button>
                      )
                    }
                  />
                ),
              }}
            />
          )}
        </div>
      </div>

      {xem && (
        <SoSanhTomTatDialog
          key={`${xem.id}-${xem.sua}`}
          id={xem.id}
          suaNgay={xem.sua}
          coTheChay={canCreate}
          coTheSua={canUpdate}
          coThePhanCong={coThePhanCong}
          dangChay={chay.isPending && chay.variables === xem.id}
          onClose={() => setXem(null)}
          onChay={chayAi}
          onXuat={xuat}
        />
      )}

      <ThemVanBanDialog
        open={moThem}
        saving={them.isPending}
        onClose={() => setMoThem(false)}
        onSubmit={(vao) =>
          them.mutate(vao, {
            onSuccess: (moi) => {
              setMoThem(false);
              if (vao.auto_summarize) setTheoDoi((cu) => ({ ...cu, [moi.id]: false }));
            },
          })
        }
      />
    </div>
  );
}
